import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Pencil, Trash2, Upload, EyeOff, Loader2, ImageIcon, Shield } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCoverImageUrl } from "@/hooks/useMediaAssets";
import { useUpdatePlaceStatus, type AdminPlaceRow } from "@/hooks/useAdminPlacesList";

interface Props {
  place: AdminPlaceRow;
  onDelete: (place: AdminPlaceRow) => void;
}

export function AdminPlacesTableRow({ place, onDelete }: Props) {
  const navigate = useNavigate();
  const coverUrl = useCoverImageUrl('place', place.id, 320);
  const statusMutation = useUpdatePlaceStatus();
  const [busy, setBusy] = useState(false);

  const isPublished = place.status === "public";
  const editUrl = `/app/admin/places/${place.id}/edit`;

  const handleToggleStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await statusMutation.mutateAsync({ id: place.id, newStatus: isPublished ? "draft" : "public" });
    } finally {
      setBusy(false);
    }
  };

  const handleRowClick = () => {
    navigate(editUrl);
  };

  return (
    <TableRow onClick={handleRowClick} className="cursor-pointer">
      <TableCell className="w-[60px] pr-0">
        {coverUrl ? (
          <img src={coverUrl} alt="" loading="lazy" className="h-10 w-10 rounded object-cover bg-muted" />
        ) : (
          <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      <TableCell className="font-medium">
        <span className="text-primary hover:underline">{place.name}</span>
        {place.editor_count > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center ml-1.5 align-middle">
                <Shield className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{place.editor_count} ambassadör{place.editor_count !== 1 ? 'er' : ''}</TooltipContent>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>{place.municipality || "–"}</TableCell>
      <TableCell>{place.landskap || "–"}</TableCell>
      <TableCell>
        <Badge variant={isPublished ? "default" : "secondary"}>
          {isPublished ? "Publicerad" : place.status === "archived" ? "Arkiverad" : "Utkast"}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant={isPublished ? "outline" : "default"} size="sm" disabled={busy} onClick={handleToggleStatus} className="h-7 text-xs gap-1">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : isPublished ? <EyeOff className="h-3 w-3" /> : <Upload className="h-3 w-3" />}
            {isPublished ? "Avpublicera" : "Publicera"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(editUrl)}><Pencil className="h-4 w-4 mr-2" />Redigera</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive" onClick={() => onDelete(place)}><Trash2 className="h-4 w-4 mr-2" />Radera</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
